import React from 'react';
import { Building2, Calendar, DollarSign, FileText, Users } from 'lucide-react';
import { useDeals } from '../store/DealsContext';
import { Deal } from '../types';

export default function DealForm() {
  const { addDeal } = useDeals();
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const newDeal: Deal = {
      id: crypto.randomUUID(),
      name: formData.get('dealName') as string,
      status: 'prospect',
      type: formData.get('dealType') as 'acquisition' | 'merger' | 'divestiture',
      value: Number(formData.get('dealValue')),
      description: formData.get('description') as string,
      targetCompany: {
        id: crypto.randomUUID(),
        name: formData.get('targetName') as string,
        industry: formData.get('targetIndustry') as string,
        revenue: 0,
        employees: 0,
        location: '',
        description: ''
      },
      acquiringCompany: {
        id: crypto.randomUUID(),
        name: formData.get('acquiringName') as string,
        industry: formData.get('acquiringIndustry') as string,
        revenue: 0,
        employees: 0,
        location: '',
        description: ''
      },
      startDate: formData.get('startDate') as string,
      expectedCloseDate: formData.get('expectedCloseDate') as string,
      team: [],
      documents: [],
      tasks: []
    };
    
    addDeal(newDeal);
    (e.target as HTMLFormElement).reset();
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">New Deal Entry</h2>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Deal Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Basic Deal Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Deal Name</label>
              <input
                name="dealName"
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter deal name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Deal Type</label>
              <select
                name="dealType"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="acquisition">Acquisition</option>
                <option value="merger">Merger</option>
                <option value="divestiture">Divestiture</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Deal Value</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  name="dealValue"
                  type="number"
                  required
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Company Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <h4 className="font-medium text-gray-700">Target Company</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input
                  name="targetName"
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Industry</label>
                <input
                  name="targetIndustry"
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="space-y-6">
              <h4 className="font-medium text-gray-700">Acquiring Company</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input
                  name="acquiringName"
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Industry</label>
                <input
                  name="acquiringIndustry"
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Timeline
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                name="startDate"
                type="date"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Expected Close Date</label>
              <input
                name="expectedCloseDate"
                type="date"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Additional Information
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Deal Description</label>
            <textarea
              name="description"
              rows={4}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter detailed description of the deal..."
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create Deal
          </button>
        </div>
      </form>
    </div>
  );
}